import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Pagination,
  CircularProgress,
  Tooltip,
  Alert,
  Avatar,
  Container,
  Fade,
  // Zoom,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Person,
  PersonAdd,
  Refresh,
  AdminPanelSettings,
  Security,
  Group,
  PeopleAlt,
  CheckCircle,
  LockReset,
  VerifiedUser,
  GppBad
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface User {
  id: number;
  email: string;
  role: string;
  department?: string;
  job_title?: string;
  is_active: boolean;
  two_factor_enabled: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

interface UserListResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    email: '',
    role: '',
    is_active: true
  });
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    role: 'user',
    department: '',
    job_title: ''
  });

  // Super admin action states
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [toggle2FADialogOpen, setToggle2FADialogOpen] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [actionUser, setActionUser] = useState<User | null>(null);

  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, searchQuery, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('is_active', statusFilter);

      const response = await axios.get<UserListResponse>(`/api/admin/users?${params}`);

      setUsers(response.data.users || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalUsers(response.data.pagination?.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleRoleFilter = (event: any) => {
    setRoleFilter(event.target.value);
    setPage(1);
  };

  const handleStatusFilter = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email,
      role: user.role,
      is_active: user.is_active
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      await axios.put(`/api/admin/users/${selectedUser.id}`, editForm);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password) {
      toast.error('Email and password are required');
      return;
    }

    try {
      await axios.post('/api/admin/users', createForm);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setCreateForm({
        email: '',
        password: '',
        role: 'user',
        department: '',
        job_title: ''
      });
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleDeactivateUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/users/${userId}`);
      toast.success('User deactivated successfully');
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const handleOpenResetPassword = (user: User) => {
    setActionUser(user);
    setResetPasswordForm({ new_password: '', confirm_password: '' });
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!actionUser) return;
    if (resetPasswordForm.new_password !== resetPasswordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (resetPasswordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await axios.post(`/api/admin/users/${actionUser.id}/reset-password`, {
        new_password: resetPasswordForm.new_password
      });
      toast.success(`Password reset for ${actionUser.email}`);
      setResetPasswordDialogOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleOpenToggle2FA = (user: User) => {
    setActionUser(user);
    setToggle2FADialogOpen(true);
  };

  const handleToggle2FA = async (enable: boolean) => {
    if (!actionUser) return;
    try {
      await axios.post(`/api/admin/users/${actionUser.id}/toggle-2fa`, { enabled: enable });
      toast.success(`2FA ${enable ? 'enabled' : 'disabled'} for ${actionUser.email}`);
      setToggle2FADialogOpen(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to toggle 2FA');
    }
  };

  const roleHierarchy: Record<string, number> = {
    user: 1,
    team_member: 2,
    team_lead: 3,
    department_head: 4,
    moderator: 5,
    admin: 6,
    super_admin: 7
  };

  const canManageUser = (targetRole: string) => {
    if (!currentUser) return false;
    const myLevel = roleHierarchy[currentUser.role] || 0;
    const targetLevel = roleHierarchy[targetRole] || 0;
    return myLevel > targetLevel;
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'super_admin':
        return 'error';
      case 'moderator':
        return 'warning';
      case 'team_lead':
        return 'info';
      case 'department_head':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
      case 'super_admin':
        return <AdminPanelSettings />;
      case 'moderator':
        return <Security />;
      case 'team_lead':
      case 'department_head':
        return <Group />;
      default:
        return <Person />;
    }
  };

  if (currentUser?.role !== 'admin' && currentUser?.role !== 'super_admin') {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Access denied. You need admin privileges to access user management.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Enhanced Header with Gradient Background */}
        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 4,
              mb: 4,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <PeopleAlt sx={{ fontSize: 36 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" gutterBottom>
                    User Management
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.95 }}>
                    Manage all users in the system. Search, filter, and update user accounts.
                  </Typography>
                </Box>
                {!isMobile && (
                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PersonAdd />}
                      onClick={() => setCreateDialogOpen(true)}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)'
                        }
                      }}
                    >
                      Create User
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<Refresh />}
                      onClick={fetchUsers}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Refresh
                    </Button>
                  </Box>
                )}
              </Box>
              {isMobile && (
                <Box display="flex" gap={2} mt={2}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<PersonAdd />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)'
                      }
                    }}
                  >
                    Create
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    startIcon={<Refresh />}
                    onClick={fetchUsers}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Fade>

        {/* Enhanced Filters Section */}
        <Fade in timeout={800}>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Search sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Search & Filters
              </Typography>
            </Box>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search Users"
                  placeholder="Search by email..."
                  value={searchQuery}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select value={roleFilter} onChange={handleRoleFilter}>
                    <MenuItem value="">All Roles</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="team_lead">Team Lead</MenuItem>
                    <MenuItem value="department_head">Department Head</MenuItem>
                    <MenuItem value="moderator">Moderator</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    {currentUser?.role === 'super_admin' && (
                      <MenuItem value="super_admin">Super Admin</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} onChange={handleStatusFilter}>
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    borderRadius: 2,
                    bgcolor: isDark ? '#0f172a' : 'primary.light',
                    borderColor: isDark ? '#1e293b' : 'primary.main'
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={isDark ? '#60a5fa' : 'primary.main'}
                  >
                    {totalUsers}
                  </Typography>
                  <Typography variant="caption" color={isDark ? '#94a3b8' : 'text.secondary'}>
                    Total Users
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Enhanced Users Table */}
        <Fade in timeout={1000}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}
                >
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Department</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Login</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={60} />
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                        Loading users...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: 'grey.100',
                          color: 'text.secondary',
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        <PeopleAlt sx={{ fontSize: 48 }} />
                      </Avatar>
                      <Typography variant="h6" gutterBottom fontWeight="bold">
                        No users found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Try adjusting your search or filter criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            sx={{
                              bgcolor: user.is_active ? 'primary.light' : 'grey.300'
                            }}
                          >
                            {getRoleIcon(user.role)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.email}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.job_title || 'No title'} • ID: {user.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getRoleIcon(user.role)}
                          label={user.role.replace('_', ' ').toUpperCase()}
                          color={getRoleColor(user.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.department || 'Not assigned'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Active' : 'Inactive'}
                          color={user.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.last_login
                            ? new Date(user.last_login).toLocaleDateString()
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(user.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Edit user">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                              sx={{
                                bgcolor: 'action.hover',
                                '&:hover': {
                                  bgcolor: 'primary.light',
                                  color: 'primary.main'
                                }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin') &&
                            user.id !== currentUser?.id &&
                            canManageUser(user.role) && (
                              <>
                                <Tooltip title="Reset password">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenResetPassword(user)}
                                    sx={{
                                      bgcolor: 'action.hover',
                                      '&:hover': { bgcolor: 'warning.light', color: 'warning.dark' }
                                    }}
                                  >
                                    <LockReset fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip
                                  title={user.two_factor_enabled ? 'Disable 2FA' : 'Enable 2FA'}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenToggle2FA(user)}
                                    sx={{
                                      bgcolor: 'action.hover',
                                      '&:hover': {
                                        bgcolor: user.two_factor_enabled
                                          ? 'error.light'
                                          : 'success.light',
                                        color: user.two_factor_enabled
                                          ? 'error.dark'
                                          : 'success.dark'
                                      }
                                    }}
                                  >
                                    {user.two_factor_enabled ? (
                                      <GppBad fontSize="small" />
                                    ) : (
                                      <VerifiedUser fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          {user.id !== currentUser?.id && (
                            <Tooltip title="Deactivate user">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeactivateUser(user.id)}
                                disabled={!user.is_active}
                                sx={{
                                  bgcolor: 'action.hover',
                                  '&:hover': {
                                    bgcolor: 'error.light'
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Fade>

        {/* Pagination */}
        {totalPages > 1 && (
          <Fade in timeout={1200}>
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                size={isMobile ? 'small' : 'large'}
                showFirstButton
                showLastButton
              />
            </Box>
          </Fade>
        )}

        {/* Edit User Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <Edit />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Edit User
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedUser?.email}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="user@example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="team_lead">Team Lead</MenuItem>
                    <MenuItem value="department_head">Department Head</MenuItem>
                    <MenuItem value="moderator">Moderator</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    {currentUser?.role === 'super_admin' && (
                      <MenuItem value="super_admin">Super Admin</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editForm.is_active.toString()}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_active: e.target.value === 'true' })
                    }
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setEditDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={handleUpdateUser}
              variant="contained"
              size="large"
              startIcon={<CheckCircle />}
            >
              Update User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <PersonAdd />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Create New User
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Add a new user to the system
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  required
                  placeholder="user@example.com"
                  helperText="User will use this email to login"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  required
                  placeholder="Enter secure password"
                  helperText="Minimum 8 characters"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="team_lead">Team Lead</MenuItem>
                    <MenuItem value="department_head">Department Head</MenuItem>
                    <MenuItem value="moderator">Moderator</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    {currentUser?.role === 'super_admin' && (
                      <MenuItem value="super_admin">Super Admin</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Optional Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Department (Optional)"
                  value={createForm.department}
                  onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })}
                  placeholder="e.g., Engineering, Sales, HR"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Job Title (Optional)"
                  value={createForm.job_title}
                  onChange={(e) => setCreateForm({ ...createForm, job_title: e.target.value })}
                  placeholder="e.g., Software Engineer, Manager"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setCreateDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              variant="contained"
              disabled={!createForm.email || !createForm.password}
              size="large"
              startIcon={<PersonAdd />}
            >
              Create User
            </Button>
          </DialogActions>
        </Dialog>
        {/* Reset Password Dialog */}
        <Dialog
          open={resetPasswordDialogOpen}
          onClose={() => setResetPasswordDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <LockReset />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Reset Password
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {actionUser?.email}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={resetPasswordForm.new_password}
                  onChange={(e) =>
                    setResetPasswordForm({ ...resetPasswordForm, new_password: e.target.value })
                  }
                  helperText="Minimum 8 characters"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={resetPasswordForm.confirm_password}
                  onChange={(e) =>
                    setResetPasswordForm({ ...resetPasswordForm, confirm_password: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setResetPasswordDialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              color="warning"
              size="large"
              startIcon={<LockReset />}
              disabled={!resetPasswordForm.new_password || !resetPasswordForm.confirm_password}
            >
              Reset Password
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toggle 2FA Dialog */}
        <Dialog
          open={toggle2FADialogOpen}
          onClose={() => setToggle2FADialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{ bgcolor: actionUser?.two_factor_enabled ? 'error.main' : 'success.main' }}
              >
                {actionUser?.two_factor_enabled ? <GppBad /> : <VerifiedUser />}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {actionUser?.two_factor_enabled ? 'Disable 2FA' : 'Enable 2FA'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {actionUser?.email}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Alert
              severity={actionUser?.two_factor_enabled ? 'warning' : 'info'}
              sx={{ borderRadius: 2 }}
            >
              {actionUser?.two_factor_enabled
                ? 'Disabling 2FA will remove the extra security layer for this user. They will be able to log in with just their password.'
                : 'Enabling 2FA will require this user to verify their identity via email code on every login.'}
            </Alert>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setToggle2FADialogOpen(false)} size="large">
              Cancel
            </Button>
            <Button
              onClick={() => handleToggle2FA(!actionUser?.two_factor_enabled)}
              variant="contained"
              color={actionUser?.two_factor_enabled ? 'error' : 'success'}
              size="large"
            >
              {actionUser?.two_factor_enabled ? 'Disable 2FA' : 'Enable 2FA'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default UserManagement;
